/**
 * Opportunities and projects — the sheet one is created in, and worked in.
 *
 * 🔴 **TWO WAYS OF WORKING, ONE RECORD** (Callum, 2026-09-17). An OPPORTUNITY is a
 * single deal — one account, one decision, and ⚠️ **exactly what QX has always meant
 * by the word**. A PROJECT is the bigger piece — a programme, a bid — that holds
 * several opportunities, parts and accounts. The scored list on /opportunities
 * SUGGESTS opportunities; "Work this" creates one here.
 *
 * Like the log sheet, every create sends a client-generated UUID, so pressing Save
 * again after a dropped connection returns the same record rather than a second one.
 * Saves dispatch `sales:work-changed` ({entity, code}) for the account page's tabs
 * and `sales:items-changed` for the lists.
 */
import { escapeHtml, getIcon, router, showError, showModal, showSuccess } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { accountUrl, partUrl } from '../lib/links.js';
import { fig, money, shortDate } from '../lib/fig.js';
import { accountPicker, failed, newId, saving, segment, wireSegment } from './log-sheet.js';
import { openQuoteSheet } from './quote-sheet.js';
import { openMeetingSetup } from './meeting-setup.js';
import { suppliersSection, wireSuppliers } from './work-suppliers.js';
import { journeyStrip } from '../lib/journey.js';
import { attachmentLink, mountAttachments } from './attachments.js';
import { mentionInput } from './mention-input.js';

export const STAGES = [
  ['new', 'New'], ['qualified', 'Qualified'], ['quoted', 'Quoted'],
  ['negotiating', 'Negotiating'], ['won', 'Won'], ['lost', 'Lost'],
];
export const STAGE_LABEL = Object.fromEntries(STAGES);
export const OPEN_STAGES = ['new', 'qualified', 'quoted', 'negotiating'];
const LOST_REASONS = [['price', 'Price'], ['lead_time', 'Lead time'], ['stock', 'Stock'], ['spec', 'Spec'],
  ['competitor', 'Competitor'], ['no_decision', 'No decision'], ['other', 'Other']];
const ROLES = [['customer', 'Customer'], ['end_user', 'End user'], ['distributor', 'Distributor'],
  ['contractor', 'Contractor'], ['specifier', 'Specifier'], ['other', 'Other']];
const ROLE_LABEL = Object.fromEntries(ROLES);
const HOME_CURRENCY = { uk: 'GBP', us: 'USD', de: 'EUR' };
const ENTITY = { uk: 'UK', us: 'US', de: 'DE' };

export const KIND_LABEL = { opportunity: 'Opportunity', project: 'Project' };
const KIND_HINT = {
  opportunity: 'An opportunity is one deal with one account — what QX calls an opportunity. '
    + 'If it is a programme with several deals in it, make a project instead.',
  project: 'A project is a bigger piece of work — a programme or a bid. It can hold several '
    + 'opportunities, parts and accounts.',
};

function changed(item) {
  window.dispatchEvent(new CustomEvent('sales:items-changed', { detail: { id: item && item.id } }));
  if (item && item.customer_code) {
    window.dispatchEvent(new CustomEvent('sales:work-changed',
      { detail: { entity: item.entity, code: item.customer_code } }));
  }
}

export function stageBadge(stage) {
  const colour = stage === 'won' ? 'green' : stage === 'lost' ? 'red'
    : stage === 'negotiating' || stage === 'quoted' ? 'blue' : 'gray';
  return `<span class="badge badge-${colour}">${escapeHtml(STAGE_LABEL[stage] || stage)}</span>`;
}

export function itemValue(p) {
  return p.value == null ? '' : money(p.currency || HOME_CURRENCY[p.entity] || '', 0)(p.value);
}

/** New opportunity or project. `account` = {entity, code, name} or null. */
export function openWorkSheet(kind = 'opportunity', account = null, prefill = {}) {
  const id = newId();
  const state = { account, stage: prefill.stage || 'new' };
  const content = document.createElement('div');
  content.innerHTML = `
    <p class="sc-sheet-hint">${getIcon('info', 12)} ${escapeHtml(KIND_HINT[kind])}</p>
    <div class="form-group"><label class="form-label" for="sc-proj-title">What is it?</label>
      <input class="form-input" id="sc-proj-title" maxlength="200"
             placeholder="${kind === 'project' ? 'e.g. Type 26 programme' : 'e.g. Harness refresh — 4 parts'}"
             value="${escapeHtml(prefill.title || '')}"></div>
    ${prefill.prospect_id
    ? `<div class="form-group"><label class="form-label">For</label><div class="sc-picked">${getIcon('user-plus', 16)}
        <strong>${escapeHtml(prefill.prospect_name || 'this prospect')}</strong>
        <span class="badge badge-amber">Not in QX yet</span></div></div>`
    : '<div class="form-group"><label class="form-label">Account</label><div data-account></div></div>'}
    <div class="form-group"><label class="form-label">Stage</label>${segment('stage', STAGES.filter(s => OPEN_STAGES.includes(s[0])), prefill.stage || 'new')}</div>
    <div class="form-row" style="display:flex;gap:var(--space-3);flex-wrap:wrap">
      <div class="form-group" style="flex:1;min-width:140px"><label class="form-label" for="sc-proj-value">Value</label>
        <input class="form-input" id="sc-proj-value" type="number" min="0" step="0.01" placeholder="0.00" value="${escapeHtml(prefill.value != null ? String(prefill.value) : '')}"></div>
      <div class="form-group" style="flex:1;min-width:120px"><label class="form-label" for="sc-proj-ccy">Currency</label>
        <select class="form-select" id="sc-proj-ccy">
          ${['GBP', 'USD', 'EUR', 'SEK', 'NOK'].map(c => `<option value="${c}">${c}</option>`).join('')}
        </select></div>
      <div class="form-group" style="flex:1;min-width:150px"><label class="form-label" for="sc-proj-close">Expected close</label>
        <input class="form-input" id="sc-proj-close" type="date" value="${escapeHtml(prefill.expected_close || '')}"></div>
    </div>
    <div class="form-group"><label class="form-label" for="sc-proj-qx">QX opportunity number
      <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <input class="form-input" id="sc-proj-qx" maxlength="50" placeholder="If you keep one in QX"></div>
    <div class="form-group"><label class="form-label" for="sc-proj-note">Note</label>
      <textarea class="form-textarea" id="sc-proj-note" rows="3" maxlength="20000"
        placeholder="What is being bid, who else is involved, what they need">${escapeHtml(prefill.note || '')}</textarea></div>`;

  const modal = showModal({
    title: `New ${KIND_LABEL[kind].toLowerCase()}`, content, size: 'md',
    actions: [
      { label: 'Cancel', className: 'btn btn-secondary' },
      { label: 'Save', className: 'btn btn-primary', onClick: (close) => save(close) },
    ],
  });

  const ccy = content.querySelector('#sc-proj-ccy');
  const setCurrency = (a) => { ccy.value = HOME_CURRENCY[(a || {}).entity] || 'GBP'; };
  setCurrency(account || (prefill.prospect_id ? { entity: prefill.entity } : null));
  if (prefill.currency) ccy.value = prefill.currency;
  // a PROSPECT (CRM #6) has no account yet: the deal belongs to the prospect, and moves to
  // the account when somebody links the prospect to its QX code
  if (!prefill.prospect_id) accountPicker(content, account, (a) => { state.account = a; if (a) setCurrency(a); });
  wireSegment(content, 'stage', v => { state.stage = v || 'new'; });
  setTimeout(() => content.querySelector('#sc-proj-title').focus(), 80);

  let busy = false;
  async function save(close) {
    if (busy) return;
    const title = content.querySelector('#sc-proj-title').value.trim();
    if (!title) { showError(`Say what the ${KIND_LABEL[kind].toLowerCase()} is.`); return; }
    if (!state.account && !prefill.prospect_id) { showError('Choose the account it is for.'); return; }
    const value = content.querySelector('#sc-proj-value').value;
    const body = {
      id, title, stage: state.stage,
      ...(state.account ? { entity: state.account.entity, customer_code: state.account.code }
        : { entity: prefill.entity, prospect_id: prefill.prospect_id }),
      value: value === '' ? undefined : value,
      currency: value === '' ? undefined : ccy.value,
      expected_close: content.querySelector('#sc-proj-close').value || undefined,
      qx_ref: content.querySelector('#sc-proj-qx').value.trim() || undefined,
      note: content.querySelector('#sc-proj-note').value || undefined,
      parent_id: prefill.parent_id || undefined,
      quotes: prefill.quotes || undefined,
    };
    busy = true;
    saving(modal, true, 'Save');
    try {
      const res = await salesApi.createWorkItem(kind, body);
      close();
      showSuccess(res.replayed ? 'Already saved.' : `${KIND_LABEL[kind]} created.`);
      changed(res.data);
      openWorkItem(res.data.id);
    } catch (err) {
      failed(err, `The ${KIND_LABEL[kind].toLowerCase()}`);
      saving(modal, false, 'Save');
    } finally {
      busy = false;
    }
  }
  return modal;
}

// ── The detail sheet ──────────────────────────────────────────────────

function partLine(p = {}) {
  return `<div class="sc-part-line" data-line>
      <input class="form-input" data-part maxlength="120" placeholder="Part number" value="${escapeHtml(p.part || '')}">
      <input class="form-input" data-qty type="number" min="0" step="0.001" placeholder="Qty" value="${p.qty ?? ''}">
      <input class="form-input" data-price type="number" min="0" step="0.0001" placeholder="Target" value="${p.target_price ?? ''}">
      <button type="button" class="btn btn-ghost btn-sm" data-drop aria-label="Remove line">${getIcon('x', 14)}</button>
    </div>`;
}

function partsEditor(rows) {
  return `<div data-parts>${(rows.length ? rows : [{}]).map(partLine).join('')}</div>
    <button type="button" class="btn btn-ghost btn-sm" data-add-part>${getIcon('plus', 14)} Add a part</button>`;
}

function readParts(root) {
  return [...root.querySelectorAll('[data-parts] [data-line]')].map(el => ({
    part: el.querySelector('[data-part]').value.trim(),
    qty: el.querySelector('[data-qty]').value || undefined,
    target_price: el.querySelector('[data-price]').value || undefined,
  })).filter(p => p.part);
}

const RESOLUTION = {
  ours: ['green', 'Ours'], their_number: ['blue', 'Their number'], register: ['blue', 'Register'],
  unresolved: ['amber', 'Not placed'],
};
const STATE = {
  invoiced: ['green', 'Invoiced since this started'], on_order: ['green', 'On order'],
  quoted: ['blue', 'On a live quote'], bought_before: ['gray', 'Bought before'],
  new_to_them: ['amber', 'New to them'],
};
const PROMISE = { yes: ['green', 'Can promise'], likely: ['blue', 'Likely'], no: ['red', 'Short'],
  unknown: ['gray', 'Supply not known'] };

function badge([tone, label], title) {
  return `<span class="badge badge-${tone}"${title ? ` title="${escapeHtml(title)}"` : ''}>${escapeHtml(label)}</span>`;
}

function resolvedTable(rows, p, d) {
  if (!rows.length) return '<p class="sc-proj-sub">None listed.</p>';
  const cur = HOME_CURRENCY[p.entity];
  const body = rows.map(r => {
    const a = r.account || {};
    const lp = a.last_paid;
    const q = (a.live_quotes || [])[0];
    const o = (a.open_orders || [])[0];
    const detail = [
      q ? `quote ${escapeHtml(q.quote_no)} expires ${escapeHtml(shortDate(q.expires))}` : '',
      o ? `order ${escapeHtml(o.order_no)}${o.late ? ' (late)' : ''}` : '',
      a.since_start ? `${escapeHtml(String(a.since_start.invoices))} invoice${a.since_start.invoices === 1 ? '' : 's'} since ${escapeHtml(shortDate(d.started))}` : '',
    ].filter(Boolean).join(' · ');
    const promise = r.promise ? badge(PROMISE[r.promise.answer] || ['gray', r.promise.answer], (r.promise.basis || []).join(' ')) : '';
    return `<tr>
      <td><div>${r.productseq
        ? `<a href="${partUrl(r.productseq, p.entity, { customer: p.customer_code, qty: r.qty || undefined })}" data-link>${escapeHtml(r.productseq)}</a>`
        : escapeHtml(r.typed)} ${badge(RESOLUTION[r.resolution] || ['gray', r.resolution])}</div>
        ${r.productseq && r.productseq !== r.typed ? `<div class="sc-proj-sub">typed as ${escapeHtml(r.typed)}</div>` : ''}
        ${r.description ? `<div class="sc-proj-sub">${escapeHtml(r.description)}</div>` : ''}
        ${r.note ? `<div class="sc-proj-sub">${escapeHtml(r.note)}</div>` : ''}
        ${(r.suggestions || []).length ? `<div class="sc-proj-sub">Similar: ${r.suggestions.map(s => s.in_catalogue
          ? `<a href="${partUrl(s.part, p.entity)}" data-link>${escapeHtml(s.part)}</a> (${escapeHtml(s.relationship)})`
          : `${escapeHtml(s.part)} (${escapeHtml(s.relationship)}, not ours)`).join(', ')}</div>` : ''}</td>
      <td style="text-align:right">${r.productseq ? fig(r.free, { format: (v) => Number(v).toLocaleString('en-GB'),
        reason: 'No stock rows for this part.' }) : ''}${r.qty ? `<div class="sc-proj-sub" style="justify-content:flex-end">
        need ${escapeHtml(Number(r.qty).toLocaleString('en-GB'))} ${promise}</div>` : ''}</td>
      <td style="text-align:right">${lp ? `${fig(lp.unit_price, { format: money(cur, 4) })}
        <div class="sc-proj-sub" style="justify-content:flex-end">${escapeHtml(shortDate(lp.date))}</div>` : (r.productseq && p.customer_code ? '<span class="sc-proj-sub">never</span>' : '')}</td>
      <td>${r.state ? badge(STATE[r.state] || ['gray', r.state]) : ''}${detail ? `<div class="sc-proj-sub">${detail}</div>` : ''}</td>
    </tr>`;
  }).join('');
  const s = d.summary || {};
  return `<p class="sc-proj-sub">${escapeHtml(String(s.resolved || 0))} of ${escapeHtml(String(s.lines || rows.length))}
      placed on our catalogue · ERP as of ${escapeHtml(shortDate(d.as_of))}${p.kind === 'project' && p.customer_code ? ' · status is for the main account' : ''}</p>
    <div style="overflow-x:auto"><table class="data-table">
      <thead><tr><th>Part</th><th style="text-align:right">Free now</th><th style="text-align:right">They last paid</th><th>With them</th></tr></thead>
      <tbody>${body}</tbody></table></div>`;
}

function detailBody(p, writer) {
  const parts = p.parts || [];
  const quotes = p.quotes || [];
  const others = p.accounts || [];
  const children = p.children || [];
  const isProject = p.kind === 'project';
  return `
    <div class="sc-proj-head">
      <div>
        <h3 style="margin:0">${escapeHtml(p.title)}</h3>
        <div class="sc-proj-sub">
          <span class="badge badge-${isProject ? 'blue' : 'gray'}">${escapeHtml(KIND_LABEL[p.kind] || p.kind)}</span>
          ${p.customer_code ? `<a href="${accountUrl(p.customer_code, p.entity)}" data-goto-account>
            ${getIcon('users', 14)} ${escapeHtml(p.customer_name || p.customer_code)}</a>` : ''}
          <span class="badge badge-gray">${escapeHtml(ENTITY[p.entity] || p.entity)}</span>
          ${p.qx_ref ? `<span class="badge badge-gray" title="Its number in QX">QX ${escapeHtml(p.qx_ref)}</span>` : ''}
        </div>
      </div>
      <div style="text-align:right">
        <div style="font-size:var(--text-lg);font-weight:600">${escapeHtml(itemValue(p) || '—')}</div>
        <div style="font-size:var(--text-xs);color:${p.overdue ? 'var(--red-600)' : 'var(--text-tertiary)'}">
          ${p.expected_close ? `${p.overdue ? 'was due ' : 'close '}${escapeHtml(shortDate(p.expected_close))}` : 'no close date'}</div>
      </div>
    </div>

    <div class="form-group"><label class="form-label">Stage</label>
      ${writer ? segment('stage', STAGES, p.stage) : stageBadge(p.stage)}
      ${p.lost_reason ? `<div class="sc-proj-sub" style="margin-top:var(--space-2)">Lost: ${escapeHtml(
        (LOST_REASONS.find(r => r[0] === p.lost_reason) || [, p.lost_reason])[1])}</div>` : ''}
    </div>

    ${OPEN_STAGES.includes(p.stage) ? `<div class="form-group"><label class="form-label">Next step</label>
      <div data-next-step></div></div>` : ''}

    ${p.customer_code ? `<div class="form-group"><label class="form-label">People on this ${isProject ? 'project' : 'deal'}</label>
      <div data-people><p class="sc-proj-sub">Loading…</p></div></div>` : ''}

    <div class="form-group"><label class="form-label">How far it has got</label>
      <div data-journey><span class="sc-proj-sub">Checking quotes, orders and invoices…</span></div></div>

    <div class="form-group"><label class="form-label">Timeline</label>
      ${writer ? `<div class="sc-tl-add" style="display:flex;flex-direction:column;gap:var(--space-2);margin-bottom:var(--space-3)">
        <!-- @mentions (CRM #7): mentionInput wraps this textarea in paint() when the deal has an account -->
        <textarea class="form-input" data-note rows="2" maxlength="20000"
          placeholder="Add a note — what was said, what happens next…"></textarea>
        <div style="display:flex;gap:var(--space-2);align-items:center;flex-wrap:wrap">
          <select class="form-select" data-note-kind style="width:auto">
            <option value="note">Note</option><option value="call">Call</option><option value="email">Email</option>
            <option value="meeting">Meeting</option><option value="visit">Visit</option>
          </select>
          <button type="button" class="btn btn-primary btn-sm" data-add-note>${getIcon('plus', 14)} Add to the timeline</button>
          <span class="sc-proj-sub">Stamped with the time and your name; it is on the account's log too.</span>
        </div></div>` : ''}
      <div data-timeline><p class="sc-proj-sub">Loading the timeline…</p></div>
    </div>

    ${isProject ? `
      <div class="form-group"><label class="form-label">Opportunities in this project (${children.length})</label>
        ${children.length ? `<ul class="sc-proj-list">${children.map(c => `<li>
            <a href="#" data-open-child="${escapeHtml(c.id)}">${escapeHtml(c.title)}</a>
            ${stageBadge(c.stage)}
            <span class="sc-proj-sub">${escapeHtml(c.customer_name || c.customer_code || '')}
              ${c.value != null ? `· ${escapeHtml(itemValue({ ...c, entity: p.entity }))}` : ''}</span></li>`).join('')}</ul>`
          : `<p class="sc-proj-sub">None yet. A programme is usually several deals — add them as
             opportunities so each one keeps its own stage and quotes.</p>`}
        ${writer ? `<button type="button" class="btn btn-secondary btn-sm" data-add-child style="margin-top:var(--space-2)">
          ${getIcon('plus', 14)} Add an opportunity</button>` : ''}
        <p class="sc-proj-sub">Closing a project leaves its opportunities alone — the programme
          ending does not decide each deal.</p>
      </div>`
    : `
      <div class="form-group"><label class="form-label">Project</label>
        ${p.parent ? `<div class="sc-picked"><a href="#" data-open-parent="${escapeHtml(p.parent.id)}">
            ${getIcon('briefcase', 14)} ${escapeHtml(p.parent.title)}</a> ${stageBadge(p.parent.stage)}
            ${writer ? '<button type="button" class="btn btn-ghost btn-sm" data-unfile>Take out</button>' : ''}</div>`
          : (writer ? `<div class="sc-inline-add" data-file-box>
              <select class="form-select" data-project-pick style="flex:1"><option value="">Not in a project</option></select>
            </div>` : '<p class="sc-proj-sub">Not part of a project.</p>')}
      </div>`}

    <div class="sc-proj-grid">
      <div><span>Owner</span><strong>${escapeHtml((p.owner || {}).email || `user ${(p.owner || {}).id}`)}</strong></div>
      <div><span>Started</span><strong>${escapeHtml(shortDate(p.created_at))}</strong></div>
      <div><span>Last change</span><strong>${escapeHtml(shortDate(p.updated_at))}</strong></div>
    </div>

    ${p.note ? `<div class="form-group"><label class="form-label">Note</label>
      <div class="sc-proj-note">${escapeHtml(p.note)}</div></div>` : ''}

    <div class="form-group"><label class="form-label">Parts (${parts.length})</label>
      ${writer ? partsEditor(parts) : (parts.length ? `<ul class="sc-proj-list">${parts.map(x => `<li>${escapeHtml(x.part)}
        ${x.qty ? ` · ${escapeHtml(String(x.qty))}` : ''}</li>`).join('')}</ul>` : '<p class="sc-proj-sub">None listed.</p>')}
    </div>
    ${parts.length ? `<div class="form-group"><label class="form-label">Where each part stands</label>
      <div data-parts-resolved><p class="sc-proj-sub">Checking each part against our catalogue, stock and
        ${p.customer_code ? 'this account' : 'the ERP'}…</p></div></div>` : ''}

    <div class="form-group"><label class="form-label">Quotes (${quotes.length})</label>
      ${quotes.length ? `<ul class="sc-proj-list">${quotes.map(q => q.system === 'pricing'
        // a quote built through pricing: its number, and the PDF the customer would get
        ? `<li>${escapeHtml(`Q${String(q.ref).padStart(5, '0')}`)} <span class="badge badge-blue">PRICING</span>
            <a href="${salesApi.quotePdfUrl(q.ref)}" target="_blank" rel="noopener">${getIcon('download', 12)} PDF</a></li>`
        : `<li>${escapeHtml(q.ref)} <span class="badge badge-gray">${escapeHtml(q.system.toUpperCase())}</span></li>`).join('')}</ul>`
        : '<p class="sc-proj-sub">None linked.</p>'}
      ${writer && p.customer_code ? `<button type="button" class="btn btn-primary btn-sm" data-build-quote style="margin-top:var(--space-2)">
          ${getIcon('file-text', 14)} Build a quote${parts.length ? ` from these ${parts.length} part${parts.length === 1 ? '' : 's'}` : ''}</button>` : ''}
      ${writer ? `<div class="sc-inline-add">
        <input class="form-input" data-quote-ref maxlength="50" placeholder="QX quote number">
        <button type="button" class="btn btn-secondary btn-sm" data-add-quote>Link a QX quote</button></div>` : ''}
    </div>

    <div class="form-group"><label class="form-label">Meetings</label>
      <div data-meetings><p class="sc-proj-sub">Loading…</p></div>
      ${writer && p.customer_code ? `<button type="button" class="btn btn-secondary btn-sm" data-plan-meeting style="margin-top:var(--space-2)">
          ${getIcon('clipboard-list', 14)} Prepare for a meeting about this</button>` : ''}
    </div>

    ${p.customer_code ? `<div class="form-group"><label class="form-label">Files</label>
      <div data-files></div></div>` : ''}

    ${isProject ? suppliersSection(p, writer) : ''}

    <div class="form-group"><label class="form-label">Other accounts involved (${others.length})</label>
      ${others.length ? `<ul class="sc-proj-list">${others.map(a => `<li>
          <a href="${accountUrl(a.customer_code, a.entity)}" data-goto-account>${escapeHtml(a.customer_name || a.customer_code)}</a>
          <span class="badge badge-gray">${escapeHtml(ROLE_LABEL[a.role] || a.role)}</span></li>`).join('')}</ul>`
        : `<p class="sc-proj-sub">${isProject
            ? 'Just the one account. A programme often runs through a prime and a distributor too.'
            : 'Just the one account.'}</p>`}
      ${p.accounts_hidden ? `<p class="sc-proj-sub">${p.accounts_hidden} more you do not have access to.</p>` : ''}
      ${writer ? `<div class="sc-inline-add" data-add-account-box>
        <div data-account style="flex:1"></div>
        <select class="form-select" data-role style="max-width:150px">
          ${ROLES.map(([id, label]) => `<option value="${id}">${label}</option>`).join('')}</select>
      </div>` : ''}
    </div>

`;
}

/** Ask why, when somebody moves one to lost. Resolves to a reason or null. */
export function askWhyLost() {
  return new Promise((resolve) => {
    const content = document.createElement('div');
    content.innerHTML = `<p>Why was it lost? Every lost deal keeps a reason — it is the only way
      the pattern shows up later.</p>${segment('why', LOST_REASONS, null)}`;
    let why = null;
    const modal = showModal({
      title: 'Lost', content, size: 'sm',
      actions: [
        { label: 'Cancel', className: 'btn btn-secondary', onClick: (close) => { resolve(null); close(); } },
        { label: 'Save', className: 'btn btn-primary', onClick: (close) => {
          if (!why) { showError('Pick a reason.'); return; }
          resolve(why); close();
        } },
      ],
    });
    wireSegment(content, 'why', v => { why = v; });
    return modal;
  });
}

/** Open an opportunity or a project: read it, work it. */
export async function openWorkItem(id, { writer = true } = {}) {
  let item;
  try {
    item = (await salesApi.getWorkItem(id)).data;
  } catch (err) {
    showError(err.status === 404 ? 'That is not there (or not yours to see).'
      : 'It could not be opened just now.');
    return null;
  }

  const content = document.createElement('div');
  const modal = showModal({
    title: KIND_LABEL[item.kind] || 'Work item', content, size: 'lg',
    actions: [
      { label: 'Close', className: 'btn btn-secondary' },
      ...(writer ? [{ label: 'Save changes', className: 'btn btn-primary', onClick: (close) => saveEdits(close) }] : []),
    ],
  });

  // the meetings about this deal (meeting plans linked to it), newest first
  let resolved = [];

  /** Each part resolved server-side: which of ours, stock / promise, last paid, its state. */
  async function loadResolvedParts() {
    const box = content.querySelector('[data-parts-resolved]');
    if (!box) return;
    try {
      const res = await salesApi.getWorkItemParts(item.id);
      const d = res.data || {};
      resolved = d.rows || [];
      const reg = ((res.meta || {}).sources || {})['vault.crossref'];
      box.innerHTML = resolvedTable(resolved, item, d)
        + (reg && reg.status !== 'ok' ? `<p class="sc-proj-sub">${getIcon('alert-triangle', 12)}
            The cross-reference register did not answer, so numbers that are not ours could not be looked up.</p>` : '')
        + (d.truncated ? `<p class="sc-proj-sub">Only the first ${resolved.length} parts are checked.</p>` : '');
      box.querySelectorAll('a[data-link]').forEach(a => a.addEventListener('click', () => modal.close()));
    } catch {
      box.innerHTML = '<p class="sc-proj-sub">The parts could not be checked just now — the list above is as typed.</p>';
    }
  }

  async function loadMeetings() {
    const box = content.querySelector('[data-meetings]');
    if (!box) return;
    try {
      const rows = (await salesApi.listMeetingPlans({ entity: item.entity, work_item: item.id, limit: 20 })).data || [];
      box.innerHTML = rows.length ? `<ul class="sc-proj-list">${rows.map(r => `<li>
          <a href="/meeting-plans/${escapeHtml(r.id)}" data-open-plan>${escapeHtml(r.meeting_at ? shortDate(r.meeting_at) : 'no date')}
          · ${escapeHtml(r.meeting_kind)}</a> <span class="badge badge-${r.status === 'done' ? 'green' : r.generated ? 'blue' : 'gray'}">${escapeHtml(r.status === 'done' ? 'done' : r.generated ? 'ready' : 'not written')}</span>
          ${r.created_by && r.created_by.name ? `<span class="sc-proj-sub">${escapeHtml(r.created_by.name)}</span>` : ''}</li>`).join('')}</ul>`
        : '<p class="sc-proj-sub">No meeting planned about this yet.</p>';
      box.querySelectorAll('[data-open-plan]').forEach(a => a.addEventListener('click', (e) => {
        e.preventDefault(); modal.close(); router.navigate(a.getAttribute('href'));
      }));
    } catch {
      box.innerHTML = '<p class="sc-proj-sub">Meetings could not be loaded just now.</p>';
    }
  }

  const TL_ICON = { created: 'plus', stage: 'arrow-right', note: 'file-text', call: 'phone', email: 'mail',
    meeting: 'calendar', visit: 'map-pin', message: 'message-square', quote_sent: 'send', task: 'check-square',
    task_done: 'check-circle', quote_linked: 'link', rfq: 'inbox', meeting_plan: 'clipboard-list',
    qx_quote: 'file-text', qx_order: 'truck', qx_invoice: 'receipt', attachment: 'paperclip' };

  /** The deal, start to finish: its journey, and every note, call, task, stage and QX step. */
  async function loadTimeline() {
    const box = content.querySelector('[data-timeline]');
    const strip = content.querySelector('[data-journey]');
    if (!box) return;
    try {
      const res = await salesApi.getWorkItemTimeline(item.id);
      const d = res.data || {};
      const fmt = money(d.journey && d.journey.currency);
      if (strip) {
        const nim = ((d.journey || {}).quotes || {}).not_in_mirror;
        strip.innerHTML = journeyStrip(d.journey) + (nim ? `<div class="sc-proj-sub">${nim} linked quote number${
          nim === 1 ? ' is' : 's are'} not in the ERP mirror (yet, or mistyped).</div>` : '');
      }
      const ev = d.events || [];
      box.innerHTML = ev.length ? `<ul class="sc-timeline">${ev.map(e => {
        const when = e.date_only ? shortDate(e.at)
          : `${shortDate(e.at)} ${new Date(e.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        const isNote = ['note', 'call', 'email', 'meeting', 'visit', 'message'].includes(e.kind);
        return `<li class="${e.kind.startsWith('qx_') ? 'is-qx' : isNote ? 'is-note' : ''}">
          <div class="sc-tl-head">${getIcon(TL_ICON[e.kind] || 'zap', 13)} <strong>${escapeHtml(e.title)}</strong>
            ${e.value ? ` · ${escapeHtml(fmt(e.value))}` : ''}
            ${e.kind === 'task' && e.status === 'open' ? `<span class="badge badge-amber">open${e.due ? ` · due ${escapeHtml(shortDate(e.due))}` : ''}</span>` : ''}</div>
          <div class="sc-tl-meta">${escapeHtml(when)}${e.by && e.by.name ? ` · ${escapeHtml(e.by.name)}` : ''}${
            e.kind.startsWith('qx_') ? ' · from QX' : ''}${e.assignee && e.assignee.name ? ` · for ${escapeHtml(e.assignee.name)}` : ''}</div>
          ${e.note ? `<div class="sc-tl-note">${escapeHtml(e.note)}</div>` : ''}
          ${e.kind === 'attachment' && e.attachment ? `<div class="sc-tl-note">${attachmentLink(e.attachment)}</div>` : ''}
        </li>`;
      }).join('')}</ul>` : '<p class="sc-proj-sub">Nothing yet.</p>';
      const src = ((res.meta || {}).sources || {})['erp.commercial'];
      if (src && src.status !== 'ok') {
        box.insertAdjacentHTML('afterbegin', `<p class="sc-proj-sub">${getIcon('alert-triangle', 12)} ${escapeHtml(src.reason || 'The ERP did not answer.')}</p>`);
      }
    } catch {
      box.innerHTML = '<p class="sc-proj-sub">The timeline could not be loaded just now.</p>';
      if (strip) strip.innerHTML = '';
    }
  }

  // CRM #2 — the next step IS the deal's soonest open task: on My tasks and Today already.
  function drawNextStep() {
    const box = content.querySelector('[data-next-step]');
    if (!box) return;
    const h = item.health || {};
    const n = h.next_step;
    const soon = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
    const form = (label) => writer ? `<div class="sc-inline-add" style="flex-wrap:wrap">
        <input class="form-input" data-ns-title maxlength="200" placeholder="${escapeHtml(label)}" style="flex:1 1 260px;min-width:200px">
        <input class="form-input" type="date" data-ns-due value="${soon}" style="flex:0 0 165px;width:165px">
        <button type="button" class="btn btn-primary btn-sm" data-ns-set>Set next step</button></div>` : '';
    const flags = (h.flags || []);
    const warn = [flags.includes('quiet') ? `nothing logged for ${h.quiet_days} days` : '',
      flags.includes('past_close') ? 'past its close date' : ''].filter(Boolean).join(' · ');
    box.innerHTML = n
      ? `<div class="sc-picked" style="${n.overdue ? 'border-color:var(--red-300,#fca5a5)' : ''}">
          ${getIcon('check-square', 14)} <strong>${escapeHtml(n.title)}</strong>
          <span class="sc-proj-sub">${n.due ? `${n.overdue ? 'was due' : 'due'} ${escapeHtml(shortDate(n.due))}` : 'no date'}
            ${n.assignee && n.assignee.name ? ` · ${escapeHtml(n.assignee.name)}` : ''}</span>
          ${writer ? '<button type="button" class="btn btn-secondary btn-sm" data-ns-done style="margin-left:auto">Done</button>' : ''}
        </div>${warn ? `<p class="sc-proj-sub" style="color:var(--amber-700,#b45309)">${escapeHtml(warn)}</p>` : ''}`
      : `<p class="sc-proj-sub" style="color:var(--amber-700,#b45309)">${getIcon('alert-triangle', 12)} No next step — every open
          ${item.kind === 'project' ? 'project' : 'opportunity'} needs one, or nobody is moving it.${warn ? ` Also ${escapeHtml(warn)}.` : ''}</p>
        ${form('What happens next? e.g. Call Jo to confirm quantities')}`;
    box.querySelector('[data-ns-done]')?.addEventListener('click', async (e) => {
      e.target.disabled = true;
      try {
        await salesApi.updateTask(n.id, { status: 'done', version: n.version });
        showSuccess('Done — what is the next step now?');
        await refreshHealth();
        content.querySelector('[data-ns-title]')?.focus();
      } catch (err) { showError(err.status === 409 ? 'Someone changed that task — reloaded.' : (err.message || 'Not saved.')); refreshHealth(); }
    });
    box.querySelector('[data-ns-set]')?.addEventListener('click', () => setNextStep());
    box.querySelector('[data-ns-title]')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') setNextStep(); });
  }

  async function setNextStep() {
    const title = (content.querySelector('[data-ns-title]').value || '').trim();
    if (!title) { content.querySelector('[data-ns-title]').focus(); return; }
    const btn = content.querySelector('[data-ns-set]');
    btn.disabled = true;
    try {
      await salesApi.createTask({ id: newId(), entity: item.entity, work_item_id: item.id, title,
        due_date: content.querySelector('[data-ns-due]').value || undefined,
        assignee_user_id: (item.owner || {}).id || undefined,
        ...(item.customer_code ? { customer_code: item.customer_code } : item.lead_id ? { lead_id: item.lead_id } : {}) });
      showSuccess('Next step set — it is on their tasks and Today.');
      window.dispatchEvent(new CustomEvent('sales:work-changed', { detail: { entity: item.entity, code: item.customer_code } }));
      await refreshHealth();
    } catch (err) { showError(err.message || 'The next step was not saved.'); btn.disabled = false; }
  }

  async function refreshHealth() {
    try { item.health = ((await salesApi.getWorkItem(item.id)).data || {}).health; } catch { /* keep what we had */ }
    drawNextStep();
    loadTimeline();
  }

  // CRM #3 — who is on this deal, with their role in it (QX contacts and ours alike)
  const DEAL_ROLE = { decision_maker: 'Decision-maker', buyer: 'Buyer', technical: 'Technical', champion: 'Champion',
    blocker: 'Blocker', user: 'User', other: 'Other' };
  async function loadPeople() {
    const box = content.querySelector('[data-people]');
    if (!box) return;
    let rows, everyone = [];
    try {
      const [mine, all] = await Promise.all([salesApi.getDealPeople(item.id),
        writer ? salesApi.getAccountPeople(item.entity, item.customer_code) : Promise.resolve(null)]);
      rows = (mine.data || {}).items || [];
      everyone = ((all && all.data) || {}).items || [];
    } catch {
      box.innerHTML = '<p class="sc-proj-sub">The people could not be loaded just now.</p>';
      return;
    }
    const on = new Set(rows.map(r => r.key));
    const pickable = everyone.filter(p => !on.has(p.key) && !p.left_on);
    box.innerHTML = (rows.length ? `<ul class="sc-proj-list">${rows.map(r => `<li>
        <strong>${escapeHtml(r.name || r.email || 'Unknown')}</strong>
        ${r.role ? `<span class="badge badge-blue">${escapeHtml(DEAL_ROLE[r.role] || r.role)}</span>` : ''}
        ${r.left_on ? '<span class="badge badge-gray">has left</span>' : ''}
        <span class="sc-proj-sub">${escapeHtml([r.job_title, r.email, r.phone].filter(Boolean).join(' · '))}</span>
        ${writer ? `<button type="button" class="btn btn-ghost btn-sm" data-person-off="${escapeHtml(r.contact_id)}" aria-label="Take off">${getIcon('x', 12)}</button>` : ''}
      </li>`).join('')}</ul>` : '<p class="sc-proj-sub">Nobody named yet — who decides, who buys, who specifies it?</p>')
      + (writer ? `<div class="sc-inline-add" style="flex-wrap:wrap">
          <select class="form-select" data-person-pick style="flex:1 1 220px"><option value="">Add somebody from the account…</option>
            ${pickable.map(p => `<option value="${escapeHtml(p.key)}">${escapeHtml(p.name || p.email)}${p.job_title ? ` — ${escapeHtml(p.job_title)}` : ''}</option>`).join('')}</select>
          <select class="form-select" data-person-role style="flex:0 0 150px">
            ${Object.entries(DEAL_ROLE).map(([k, v]) => `<option value="${k}">${v}</option>`).join('')}</select>
          <button type="button" class="btn btn-secondary btn-sm" data-person-add>Add</button></div>
          ${!everyone.length ? '<p class="sc-proj-sub">The account has no contacts yet — add them on its Contacts tab.</p>' : ''}` : '');
    box.querySelector('[data-person-add]')?.addEventListener('click', async () => {
      const key = box.querySelector('[data-person-pick]').value;
      if (!key) return;
      try { await salesApi.addDealPerson(item.id, key, box.querySelector('[data-person-role]').value); loadPeople(); loadTimeline(); }
      catch (err) { showError(err.message || 'Not added.'); }
    });
    box.querySelectorAll('[data-person-off]').forEach(b => b.addEventListener('click', async () => {
      try { await salesApi.removeDealPerson(item.id, b.dataset.personOff); loadPeople(); }
      catch (err) { showError(err.message || 'Not removed.'); }
    }));
  }

  let noteMention = null;                  // @mentions on the deal note (only with an account)

  async function addNote() {
    const ta = content.querySelector('[data-note]');
    const btn = content.querySelector('[data-add-note]');
    const note = (ta.value || '').trim();
    if (!note) { ta.focus(); return; }
    const named = noteMention ? noteMention.getValue().mentions : [];
    btn.disabled = true;
    try {
      const res = await salesApi.logActivity({
        id: newId(), entity: item.entity, work_item_id: item.id, kind: content.querySelector('[data-note-kind]').value,
        note, occurred_at: new Date().toISOString(),
        ...(item.customer_code ? { customer_code: item.customer_code } : item.lead_id ? { lead_id: item.lead_id } : {}),
        ...(named.length ? { mentions: named } : {}),
      });
      ta.value = '';
      if (noteMention) noteMention.reset();
      const told = ((res && res.mentions) || {}).mentioned || [];
      showSuccess(`Added to the timeline.${told.length ? ` ${told.length} ${told.length === 1 ? 'person' : 'people'} mentioned — it is on their Today.` : ''}`);
      window.dispatchEvent(new CustomEvent('sales:work-changed', { detail: { entity: item.entity, code: item.customer_code } }));
      loadTimeline();
    } catch (err) {
      showError(err.message || 'The note was not saved.');
    } finally { btn.disabled = false; }
  }

  function paint() {
    content.innerHTML = detailBody(item, writer);
    drawNextStep();
    loadPeople();
    loadTimeline();
    const noteEl = content.querySelector('[data-note]');
    noteMention = (noteEl && item.customer_code)
      ? mentionInput(noteEl, { account: () => ({ entity: item.entity, code: item.customer_code }) }) : null;
    content.querySelector('[data-add-note]')?.addEventListener('click', () => addNote());
    content.querySelector('[data-note]')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote();       // Ctrl/⌘+Enter adds it
    });
    loadMeetings();
    if (item.customer_code) {
      mountAttachments(content.querySelector('[data-files]'), {
        entity: item.entity, customer_code: item.customer_code, work_item_id: item.id, writer,
        onChange: () => loadTimeline(),
      });
    }
    loadResolvedParts();
    if (item.kind === 'project') wireSuppliers(content, item, { writer, patch });
    content.querySelector('[data-plan-meeting]')?.addEventListener('click', () => {
      modal.close();
      openMeetingSetup({ entity: item.entity, code: item.customer_code, name: item.customer_name || item.customer_code,
        workItem: { id: item.id, title: item.title, kind: item.kind } });
    });
    content.querySelectorAll('[data-goto-account]').forEach(a => a.addEventListener('click', () => modal.close()));
    content.querySelectorAll('[data-open-child],[data-open-parent]').forEach(a => a.addEventListener('click', (e) => {
      e.preventDefault();
      modal.close();
      openWorkItem(a.dataset.openChild || a.dataset.openParent, { writer });
    }));
    if (!writer) return;
    wireSegment(content, 'stage', (v) => moveStage(v));
    content.querySelector('[data-add-part]')?.addEventListener('click', () => {
      const box = content.querySelector('[data-parts]');
      box.insertAdjacentHTML('beforeend', partLine());
      wireParts();
      box.lastElementChild.querySelector('[data-part]').focus();
    });
    content.querySelector('[data-add-quote]')?.addEventListener('click', () => addQuote());
    // Build a quote through pricing, starting from this record's own parts; the saved
    // quote comes back linked (sales:items-changed reopens this sheet's data).
    content.querySelector('[data-build-quote]')?.addEventListener('click', () => {
      modal.close();
      openQuoteSheet({ entity: item.entity, code: item.customer_code, name: item.customer_name || item.customer_code },
        { workItem: { id: item.id, title: item.title },
          // a customer's own number or a register match goes to pricing as OUR part
          lines: (item.parts || []).map((x, i) => ({ sku: (resolved[i] && resolved[i].productseq) || x.part,
            qty: x.qty || 1 })) });
    });
    content.querySelector('[data-add-child]')?.addEventListener('click', () => {
      modal.close();
      openWorkSheet('opportunity',
        item.customer_code ? { entity: item.entity, code: item.customer_code, name: item.customer_name } : null,
        { parent_id: item.id });
    });
    content.querySelector('[data-unfile]')?.addEventListener('click', () => patch({ parent_id: null }, 'Taken out of the project.'));
    fillProjectPicker();
    const picker = content.querySelector('[data-add-account-box]');
    if (picker) {
      accountPicker(picker, null, (a) => { if (a) addAccount(a, picker.querySelector('[data-role]').value); });
    }
    wireParts();
  }

  /** The open projects in this entity, so an opportunity can be filed into one. */
  async function fillProjectPicker() {
    const select = content.querySelector('[data-project-pick]');
    if (!select) return;
    try {
      const res = await salesApi.getWorkItems('project', { entity: item.entity, stage: 'open', limit: 100 });
      const items = (res.data || {}).items || [];
      if (!items.length) {
        select.parentElement.innerHTML = '<p class="sc-proj-sub">No open project to put this in yet.</p>';
        return;
      }
      select.innerHTML = '<option value="">Put this in a project…</option>'
        + items.map(x => `<option value="${escapeHtml(x.id)}">${escapeHtml(x.title)}</option>`).join('');
      select.addEventListener('change', () => {
        if (select.value) patch({ parent_id: select.value }, 'Filed into the project.');
      });
    } catch (err) {
      select.parentElement.innerHTML = '<p class="sc-proj-sub">Projects could not be listed just now.</p>';
    }
  }

  function wireParts() {
    content.querySelectorAll('[data-drop]').forEach(b => {
      b.onclick = () => {
        const lines = content.querySelectorAll('[data-parts] [data-line]');
        if (lines.length === 1) b.closest('[data-line]').querySelectorAll('input').forEach(i => { i.value = ''; });
        else b.closest('[data-line]').remove();
      };
    });
  }

  async function patch(body, what) {
    try {
      const res = await salesApi.updateWorkItem(item.id, { version: item.version, ...body });
      item = res.data;
      changed(item);
      paint();
      if (what) showSuccess(what);
      return true;
    } catch (err) {
      const payload = (err && err.data) || {};
      if (payload.code === 'version_conflict' && payload.current) {
        // Somebody else saved first. Show THEIR version rather than overwriting it.
        showError('Somebody else changed this while it was open — this is their version.');
        item = payload.current;
        paint();
      } else {
        failed(err, 'The change');
      }
      return false;
    }
  }

  async function moveStage(stage) {
    if (!stage || stage === item.stage) return;
    const body = { stage };
    if (stage === 'lost') {
      const why = await askWhyLost();
      if (!why) { paint(); return; }
      body.lost_reason = why;
    }
    await patch(body, `Moved to ${STAGE_LABEL[stage] || stage}.`);
  }

  async function addQuote() {
    const input = content.querySelector('[data-quote-ref]');
    const ref = input.value.trim();
    if (!ref) { showError('Type the quote number first.'); return; }
    const quotes = [...(item.quotes || []).map(q => ({ system: q.system, ref: q.ref })), { system: 'qx', ref }];
    await patch({ quotes }, 'Quote linked.');
  }

  async function addAccount(account, role) {
    const accounts = [...(item.accounts || []).map(a => ({ customer_code: a.customer_code, role: a.role })),
      { customer_code: account.code, role }];
    await patch({ accounts }, 'Account added.');
  }

  async function saveEdits(close) {
    const parts = readParts(content);
    const before = (item.parts || []).map(x => [x.part, x.qty, x.target_price]);
    const after = parts.map(x => [x.part, x.qty == null ? null : Number(x.qty), x.target_price == null ? null : Number(x.target_price)]);
    if (JSON.stringify(before) === JSON.stringify(after)) { close(); return; }
    if (await patch({ parts }, 'Saved.')) close();
  }

  paint();
  return modal;
}
